/**
 * Pro auth / MFA analytics (plan 475 task 2260, auth spec §8 + §12.3).
 *
 * I6 — no credential in telemetry: every payload is built FIELD BY FIELD from
 * closed unions, so an email, code, TOTP secret, passkey id/name, session id
 * or user id cannot ride along even if a caller passes one. A value outside
 * its union yields no event at all. Sends go through the shared `captureEvent`
 * seam and never run under IS_PREVIEW.
 *
 * `auth.session_started` is deliberately NOT emitted from the Pro app: the
 * consumer's method rule keys on `fva = [0, -1]` (first factor only), which a
 * TOTP-protected pro session never has, so it would report `unknown` for
 * nearly every pro sign-in.
 */
import { captureEvent, type CaptureClient, type CaptureEvent } from "./capture-event";
import { IS_PREVIEW } from "./pro-mode";
import type { MfaTrigger } from "./auth/mfa-gate";

export type AuthMfaRemedy = "setup" | "reverify";
export type AuthMfaFactor = "totp" | "passkey";
export type AuthPasskeyTrigger = "pro_setup" | "settings";
export type AuthPasskeyDeclineReason = "dismissed" | "cancelled" | "error";

const TRIGGERS: readonly MfaTrigger[] = ["acquisition", "has_clients"];
const REMEDIES: readonly AuthMfaRemedy[] = ["setup", "reverify"];
const FACTORS: readonly AuthMfaFactor[] = ["totp", "passkey"];
const PASSKEY_TRIGGERS: readonly AuthPasskeyTrigger[] = ["pro_setup", "settings"];
const DECLINE_REASONS: readonly AuthPasskeyDeclineReason[] = [
  "dismissed",
  "cancelled",
  "error",
];

export const AUTH_PRO_EVENTS = {
  mfaRequiredShown: "auth.mfa_required_shown",
  mfaSetupShown: "auth.mfa_setup_shown",
  mfaEnrolled: "auth.mfa_enrolled",
  mfaReverifyCompleted: "auth.mfa_reverify_completed",
  mfaReverifyCancelled: "auth.mfa_reverify_cancelled",
  passkeyEnrolled: "auth.passkey_enrolled",
  passkeyRemoved: "auth.passkey_removed",
  passkeyEnrollDeclined: "auth.passkey_enroll_declined",
} as const;

export type AuthEvent = CaptureEvent | null;

export function authMfaRequiredShown(
  remedy: AuthMfaRemedy,
  trigger: MfaTrigger,
): AuthEvent {
  if (!REMEDIES.includes(remedy) || !TRIGGERS.includes(trigger)) return null;
  return {
    event: AUTH_PRO_EVENTS.mfaRequiredShown,
    props: { app: "pro", remedy, trigger },
  };
}

export function authMfaSetupShown(trigger: MfaTrigger): AuthEvent {
  if (!TRIGGERS.includes(trigger)) return null;
  return { event: AUTH_PRO_EVENTS.mfaSetupShown, props: { app: "pro", trigger } };
}

export function authMfaEnrolled(factor: AuthMfaFactor): AuthEvent {
  if (!FACTORS.includes(factor)) return null;
  return { event: AUTH_PRO_EVENTS.mfaEnrolled, props: { app: "pro", factor } };
}

export function authMfaReverifyCompleted(): AuthEvent {
  return { event: AUTH_PRO_EVENTS.mfaReverifyCompleted, props: { app: "pro" } };
}

export function authMfaReverifyCancelled(): AuthEvent {
  return { event: AUTH_PRO_EVENTS.mfaReverifyCancelled, props: { app: "pro" } };
}

export function authPasskeyEnrolled(trigger: AuthPasskeyTrigger): AuthEvent {
  if (!PASSKEY_TRIGGERS.includes(trigger)) return null;
  return { event: AUTH_PRO_EVENTS.passkeyEnrolled, props: { trigger } };
}

export function authPasskeyRemoved(): AuthEvent {
  return { event: AUTH_PRO_EVENTS.passkeyRemoved, props: { app: "pro" } };
}

export function authPasskeyEnrollDeclined(
  reason: AuthPasskeyDeclineReason,
): AuthEvent {
  if (!DECLINE_REASONS.includes(reason)) return null;
  return {
    event: AUTH_PRO_EVENTS.passkeyEnrollDeclined,
    props: { trigger: "pro_setup", reason },
  };
}

/** Send a built event; null event, no client, or IS_PREVIEW is a no-op. */
export function captureAuthEvent(
  posthog: CaptureClient | null | undefined,
  evt: AuthEvent,
): void {
  if (!evt || IS_PREVIEW) return;
  captureEvent(posthog, evt);
}
